import { nameWithEmail, userDisplayName } from 'src/app/infrastructure/functions/user-name.function';
import { userDto } from 'src/assets/common/models/user-dto.g';
import _ from 'lodash';

import { countryData, CountryData } from '../../countries/models/country-data';
import { ILevelDomainData } from 'src/app/infrastructure/models/level-domain-data';
import { IDomainForm } from './domain-form';
import { IAutoComplete } from '../../shared/controls/models/auto-complete.model';
import { IDomainDto, domainDto } from 'src/assets/common/models/domain-dto.g';
import { logosDto } from 'src/assets/common/models/logos-dto.g';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { IImageParentData, imageParentData } from 'src/app/features/image/models/image-parent-data';

export interface IDomainCache {
    data: IDomainData;
    url: string;
}

export const domainData = {
    ...imageParentData,
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    subTitle: 'subTitle',
    description: 'description',
    address: 'address',
    streetNr: 'streetNr',
    postalCode: 'postalCode',
    town: 'town',
    county: 'county',
    country: 'country',
    countryCode: 'countryCode',
    countryName: 'countryName',
    // IANA-tijdzone van het domain (hoort bij het land); bepaalt de kalenderdag van lidmaatschappen.
    timeZoneId: 'timeZoneId',
    pcTown: 'pcTown',
    ownerName: 'ownerName',
    deleted: 'deleted',
    inactive: 'inactive',
    // 2FA-beleid (alleen lezen; wijzigen via PUT two-factor-policy, zie DomainDetailsComponent).
    requireTwoFactor: 'requireTwoFactor',
    // Ledenbeleid: oud-leden houden alleen-lezen toegang (Domain.FormerMembersReadOnly).
    formerMembersReadOnly: 'formerMembersReadOnly',
    requireTwoFactorFrom: 'requireTwoFactorFrom',
    // Creditsysteem (fase 3): active / readOnly / suspended.
    billingState: 'billingState',

    logos_fileShardPath: 'logoFileShardPath',
    logo_guid: 'logoGuid',
    logoThumb_guid: 'logoThumb_guid',
    logoEmail_guid: 'logoEmail_guid',
    logoIconPng_guid: 'logoIconPng_guid',
    logoIconSvg_guid: 'logoIconSvg_guid',
    logo_modifiedOn: 'logo_modifiedOn',
    logoThumb_modifiedOn: 'logoThumb_modifiedOn',
    logoEmail_modifiedOn: 'logoEmail_modifiedOn',
    logoIconPng_modifiedOn: 'logoIconPng_modifiedOn',
    logoIconSvg_modifiedOn: 'logoIconSvg_modifiedOn',
} as const;

type TDomainData = (typeof domainData)[keyof typeof domainData];

export interface IDomainData extends ILevelDomainData, IImageParentData {
    [domainData.guid]: string;
    [domainData.nr]: number | null;
    [domainData.name]: string;
    [domainData.subTitle]: string | null;
    [domainData.description]: string | null;
    [domainData.address]: string | null;
    [domainData.streetNr]: string | null;
    [domainData.postalCode]: string | null;
    [domainData.town]: string | null;
    [domainData.country]: IAutoComplete | null;
    [domainData.county]: string | null;
    [domainData.countryCode]: string | null;
    [domainData.countryName]: string | null;
    [domainData.timeZoneId]: string | null;
    [domainData.pcTown]: string | null;
    [domainData.ownerName]: string | null;
    [domainData.deleted]: boolean;
    [domainData.inactive]: boolean;
    [domainData.requireTwoFactor]: boolean;
    [domainData.formerMembersReadOnly]: boolean;
    [domainData.requireTwoFactorFrom]: string | null;
    [domainData.billingState]: string;

    [domainData.photoThumbGuid]: string | null;
    [domainData.photoOriginGuid]: string | null;
    [domainData.photoVersion]: string | null;

    [domainData.logos_fileShardPath]: string | null;
    [domainData.logo_guid]: string | null;
    [domainData.logoThumb_guid]: string | null;
    [domainData.logoEmail_guid]: string | null;
    [domainData.logoIconPng_guid]: string | null;
    [domainData.logoIconSvg_guid]: string | null;
    [domainData.logo_modifiedOn]: string | null;
    [domainData.logoThumb_modifiedOn]: string | null;
    [domainData.logoEmail_modifiedOn]: string | null;
    [domainData.logoIconPng_modifiedOn]: string | null;
    [domainData.logoIconSvg_modifiedOn]: string | null;
}

export class DomainData
    implements IDomainData, ILevelDomainData
{
    [domainData.guid]!: string;
    [domainData.nr]: number | null = null;
    [domainData.name]!: string;
    [domainData.subTitle]: string | null = null;
    [domainData.description]: string | null = null;
    [domainData.address]: string | null = null;
    [domainData.streetNr]: string | null = null;
    [domainData.postalCode]: string | null = null;
    [domainData.town]: string | null = null;
    [domainData.county]: string | null = null;
    [domainData.country]: IAutoComplete | null = null;
    [domainData.countryCode]: string | null = null;
    [domainData.countryName]: string | null = null;
    [domainData.timeZoneId]: string | null = null;
    [domainData.pcTown]: string | null = null;
    [domainData.ownerName]!: string;
    [domainData.deleted]!: boolean;
    [domainData.inactive]!: boolean;
    [domainData.requireTwoFactor]: boolean = false;
    [domainData.formerMembersReadOnly]: boolean = true;
    [domainData.requireTwoFactorFrom]: string | null = null;
    [domainData.billingState]: string = 'active';

    [domainData.photoThumbGuid]: string | null = null;
    [domainData.photoOriginGuid]: string | null = null;
    [domainData.photoVersion]: string | null = null;
    
    [domainData.logos_fileShardPath]: string | null = null;
    [domainData.logo_guid]: string | null = null;
    [domainData.logoThumb_guid]: string | null = null;
    [domainData.logoEmail_guid]: string | null = null;
    [domainData.logoIconPng_guid]: string | null = null;
    [domainData.logoIconSvg_guid]: string | null = null;
    [domainData.logo_modifiedOn]: string | null = null;
    [domainData.logoThumb_modifiedOn]: string | null = null;
    [domainData.logoEmail_modifiedOn]: string | null = null;
    [domainData.logoIconPng_modifiedOn]: string | null = null;
    [domainData.logoIconSvg_modifiedOn]: string | null = null;

    protected constructor(modelDto: IDomainDto);
    protected constructor(modelData: IDomainData, modelForm: IDomainForm);
    protected constructor(value?: IDomainDto | IDomainData | null, modelForm?: IDomainForm) {        
        if (!!modelForm) {
            const _modelData = value as IDomainData;
            _.assign(this, _modelData, modelForm);
            // countryCode afleiden uit de country-autocomplete (itemId = ISO-code): de
            // dropdown schrijft alleen het country-control, het countryCode-form-control
            // blijft leeg en zou anders via _.assign de bestaande waarde overschrijven —
            // create faalde daardoor op de verplichte CountryCode (422) en een land-wissel
            // bij edit werd nooit doorgestuurd.
            const __countryId = (this[domainData.country] as IAutoComplete | null)?.itemId;
            this[domainData.countryCode] = __countryId != null
                ? String(__countryId)
                : _modelData?.[domainData.countryCode] ?? null;
        } else {
            const _modelDto = value as IDomainDto;
            const _ownerObject = _modelDto[domainDto.ownerDto];
            
            this[domainData.guid] = _modelDto[domainDto.guid]!;
            this[domainData.nr] = Number(_modelDto[domainDto.nr]);
            this[domainData.name] = _modelDto[domainDto.name]!;
            this[domainData.subTitle] = _modelDto[domainDto.appSubTitle] ?? null;
            this[domainData.description] = _modelDto[domainDto.description] ?? null;
            this[domainData.streetNr] = _modelDto[domainDto.streetNr] ?? null;
            this[domainData.postalCode] = _modelDto[domainDto.postalCode] ?? null;
            this[domainData.town] = _modelDto[domainDto.town]!;
            this[domainData.county] = _modelDto[domainDto.county]!;
            this[domainData.countryCode] = _modelDto[domainDto.countryCode];
            this[domainData.timeZoneId] = _modelDto[domainDto.timeZoneId] ?? null;
            this[domainData.pcTown] = [this.postalCode, this.town].filter(Boolean).join(', ');
            this[domainData.ownerName] = nameWithEmail(userDisplayName(_ownerObject), _ownerObject?.[userDto.email]);
            this[domainData.deleted] = Boolean(_modelDto[domainDto.deleted]);
            this[domainData.inactive] = Boolean(_modelDto[domainDto.inactive]);
            this[domainData.requireTwoFactor] = Boolean(_modelDto[domainDto.requireTwoFactor]);
            this[domainData.formerMembersReadOnly] = _modelDto[domainDto.formerMembersReadOnly] ?? true;
            this[domainData.requireTwoFactorFrom] = _modelDto[domainDto.requireTwoFactorFrom] ?? null;
            this[domainData.billingState] = _modelDto[domainDto.billingState] ?? 'active';
            
            this[domainData.photoThumbGuid] = _modelDto[domainDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
            this[domainData.photoOriginGuid] = _modelDto[domainDto.photoBoxDto]?.[imageBoxDto.photoDto]?.[imageDto.guid] ?? null;

            this[domainData.logos_fileShardPath] = _modelDto[domainDto.logosDto]?.[logosDto.folderShard] ?? null;
            this[domainData.logo_guid] = _modelDto[domainDto.logosDto]?.[logosDto.logoDto]?.[imageDto.guid] ?? null;
            this[domainData.logoThumb_guid] = _modelDto[domainDto.logosDto]?.[logosDto.logoThumbDto]?.[imageDto.guid] ?? null;
            this[domainData.logoEmail_guid] = _modelDto[domainDto.logosDto]?.[logosDto.logoEmailDto]?.[imageDto.guid] ?? null;
            this[domainData.logoIconPng_guid] = _modelDto[domainDto.logosDto]?.[logosDto.logoIconPngDto]?.[imageDto.guid] ?? null;
            this[domainData.logoIconSvg_guid] = _modelDto[domainDto.logosDto]?.[logosDto.logoIconSvgDto]?.[imageDto.guid] ?? null;

            this[domainData.logo_modifiedOn] = _modelDto[domainDto.logosDto]?.[logosDto.logoDto]?.[imageDto.modifiedOn] ?? null;
            this[domainData.logoThumb_modifiedOn] = _modelDto[domainDto.logosDto]?.[logosDto.logoThumbDto]?.[imageDto.modifiedOn] ?? null;
            this[domainData.logoEmail_modifiedOn] = _modelDto[domainDto.logosDto]?.[logosDto.logoEmailDto]?.[imageDto.modifiedOn] ?? null;
            this[domainData.logoIconPng_modifiedOn] = _modelDto[domainDto.logosDto]?.[logosDto.logoIconPngDto]?.[imageDto.modifiedOn] ?? null;
            this[domainData.logoIconSvg_modifiedOn] = _modelDto[domainDto.logosDto]?.[logosDto.logoIconSvgDto]?.[imageDto.modifiedOn] ?? null;

            const _countryObject = CountryData.create(_modelDto[domainDto.countryDto]);
            if (!!_countryObject) {
                this[domainData.country] = {
                    itemId: _countryObject[countryData.isoCountryCode]!,
                    itemText: _countryObject[countryData.countryName]!
                };
                this[domainData.countryName] = _countryObject?.countryName ?? null;
            }
        }
    }
        
    static create(modelDto?: IDomainDto | null): DomainData | null
    static create(modelData: IDomainData, modelForm: IDomainForm): DomainData
    static create(value?: IDomainDto | IDomainData | null, modelForm?: IDomainForm): DomainData | null {

        if (!!modelForm) {
            const _modelData = value as IDomainData;
            return new DomainData(_modelData, modelForm);
        } else {
            const _modelDto = value as IDomainDto;
            return _modelDto ? new DomainData(_modelDto) : null;
        }
    }
}
